import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { 
  Upload, Camera, Shield, CheckCircle, AlertCircle, 
  FileText, Clock, Loader2,
  User, Award, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification Item Component
function VerificationItem({ 
  title, 
  description, 
  icon: Icon, 
  status, 
  country,
  onAction,
  actionLabel,
  disabled,
}) {
  const statusConfig = {
    not_started: { color: 'bg-gray-100 text-gray-600', label: 'Not Started' },
    pending_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Under Review' },
    approved: { color: 'bg-green-100 text-green-700', label: 'Verified' },
    rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
  };
  
  const config = statusConfig[status] || statusConfig.not_started;

  return (
    <div className="flex items-start gap-4 p-4 border rounded-lg hover:border-gray-300 transition-colors">
      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-indigo-600" />
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium text-gray-900">{title}</h4>
          <Badge className={`text-xs ${config.color}`}>
            {status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
            {status === 'pending_review' && <Clock className="w-3 h-3 mr-1" />}
            {config.label}
          </Badge>
        </div>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      
      {status !== 'approved' && onAction && (
        <Button 
          variant={status === 'not_started' ? 'default' : 'outline'}
          size="sm"
          onClick={onAction}
          disabled={disabled || status === 'pending_review'}
          className={status === 'not_started' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
        >
          {status === 'pending_review' ? 'In Review' : actionLabel || 'Upload'}
        </Button>
      )}
      
      {status === 'approved' && (
        <CheckCircle className="w-6 h-6 text-green-500" />
      )}
    </div>
  );
}

// Profile Photo Upload Section
function ProfilePhotoSection({ tutor, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const hasPhoto = tutor?.profile_image && !tutor.profile_image.includes('ui-avatars');
  
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      // Direct file upload to tutor photo endpoint
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/education-tuition/tutors/${tutor.id}/upload-photo`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await res.json();
      
      if (data.success) {
        // Construct full URL for the uploaded photo
        const fullPhotoUrl = data.photo_url.startsWith('http') 
          ? data.photo_url 
          : `${API_URL}${data.photo_url}`;
        onUpload && onUpload(fullPhotoUrl);
      }
    } catch (error) {
      console.error('Photo upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100">
              {tutor?.profile_image ? (
                <img 
                  src={tutor.profile_image} 
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-10 h-10 text-gray-400" />
                </div>
              )}
            </div>
            {!hasPhoto && (
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">Profile Photo</h3>
            <p className="text-sm text-gray-500 mb-3">
              {hasPhoto 
                ? 'Your profile photo is set. You can update it anytime.'
                : 'A profile photo is required to accept bookings.'}
            </p>
            
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
                <Button 
                  asChild
                  variant={hasPhoto ? 'outline' : 'default'}
                  className={!hasPhoto ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                  disabled={uploading}
                >
                  <span>
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 mr-2" />
                    )}
                    {hasPhoto ? 'Change Photo' : 'Upload Photo'}
                  </span>
                </Button>
              </label>
              
              {hasPhoto && (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Photo Set
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Document Upload Modal
function DocumentUploadModal({ isOpen, onClose, documentType, tutorId, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  
  const documentConfig = {
    dbs_basic: { title: 'DBS Basic Check', description: 'Upload your DBS Basic Disclosure certificate' },
    dbs_enhanced: { title: 'DBS Enhanced Check', description: 'Upload your DBS Enhanced certificate' },
    dbs_enhanced_barred: { title: 'DBS Enhanced with Barred List', description: 'Upload your Enhanced DBS with barred list check' },
    slts_registered: { title: 'SLTS Registration', description: 'Upload your Sri Lanka Teacher Service registration document' },
    moe_certified: { title: 'MOE Certificate', description: 'Upload your Ministry of Education certification' },
    qts: { title: 'Qualified Teacher Status', description: 'Upload your QTS certificate' },
    pgce: { title: 'PGCE Certificate', description: 'Upload your PGCE certificate' },
    degree_verified: { title: 'Degree Certificate', description: 'Upload your university degree certificate' },
    police_clearance_lk: { title: 'Police Clearance', description: 'Upload your Sri Lanka Police clearance certificate' },
  };
  
  const config = documentConfig[documentType] || { title: 'Document', description: 'Upload your document' };
  
  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      // Upload file
      const formData = new FormData();
      formData.append('files', file);
      
      const uploadRes = await fetch(`${API_URL}/api/images/upload`, {
        method: 'POST',
        body: formData,
      });
      
      const uploadData = await uploadRes.json();
      
      if (uploadData.images?.length > 0) {
        const fileUrl = `${API_URL}${uploadData.images[0].url}`;
        
        // Submit verification
        const res = await fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}/upload-document`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            document_type: documentType,
            file_url: fileUrl,
            file_name: file.name,
          }),
        });
        
        const data = await res.json();
        if (data.success) {
          onSuccess && onSuccess(data);
          onClose();
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="document-upload-modal">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <CardTitle>{config.title}</CardTitle>
          <CardDescription>{config.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            {file ? (
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-indigo-600" />
                <div className="text-left flex-1">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
                  Change
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0])}
                />
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PDF, JPG, PNG up to 10MB
                </p>
              </label>
            )}
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-700">
              <Shield className="w-4 h-4 inline mr-1" />
              Your documents are securely stored and only visible to our verification team.
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Submit
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

// Main Component
export default function TutorVerificationPage() {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  
  const [tutor, setTutor] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState({ open: false, type: null });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tutorRes, verificationsRes] = await Promise.all([
          fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}`),
          fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}/verifications`),
        ]);
        
        const [tutorData, verificationsData] = await Promise.all([
          tutorRes.json(),
          verificationsRes.json(),
        ]);
        
        if (tutorData.success) setTutor(tutorData.tutor);
        if (verificationsData.success) setVerifications(verificationsData.verifications);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (tutorId) fetchData();
  }, [tutorId]);
  
  const refetchData = async () => {
    setLoading(true);
    try {
      const [tutorRes, verificationsRes] = await Promise.all([
        fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}`),
        fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}/verifications`),
      ]);
      
      const [tutorData, verificationsData] = await Promise.all([
        tutorRes.json(),
        verificationsRes.json(),
      ]);
      
      if (tutorData.success) setTutor(tutorData.tutor);
      if (verificationsData.success) setVerifications(verificationsData.verifications);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getVerificationStatus = (type) => {
    if (!Array.isArray(verifications)) return 'not_started';
    const verification = verifications.find(v => v.type === type);
    return verification?.status || 'not_started';
  };
  
  const isUK = tutor?.country === 'UK';
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="tutor-verification-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Tutor Verification</h1>
          <p className="text-gray-500">Complete your verification to build trust with students</p>
        </div>
        
        {/* Trust Score */}
        <Card className="mb-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg mb-1">Trust Score</h3>
                <p className="text-indigo-100 text-sm">
                  Complete verifications to increase your score and attract more students
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">
                  {calculateTrustScore(tutor, verifications)}%
                </div>
                <p className="text-indigo-200 text-sm">of 100</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Profile Photo */}
        <ProfilePhotoSection 
          tutor={tutor} 
          onUpload={() => refetchData()}
        />
        
        {/* Document Verifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              Background Checks
            </CardTitle>
            <CardDescription>
              {isUK 
                ? 'DBS checks are required for tutoring minors in the UK'
                : 'Background verification for tutoring in Sri Lanka'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isUK ? (
              <>
                <VerificationItem
                  title="DBS Basic Check"
                  description="Standard criminal record check"
                  icon={Shield}
                  status={getVerificationStatus('dbs_basic')}
                  onAction={() => setUploadModal({ open: true, type: 'dbs_basic' })}
                  actionLabel="Upload Certificate"
                />
                <VerificationItem
                  title="DBS Enhanced Check"
                  description="Enhanced check for working with children"
                  icon={Shield}
                  status={getVerificationStatus('dbs_enhanced')}
                  onAction={() => setUploadModal({ open: true, type: 'dbs_enhanced' })}
                  actionLabel="Upload Certificate"
                />
              </>
            ) : (
              <>
                <VerificationItem
                  title="Police Clearance"
                  description="Sri Lanka Police clearance certificate"
                  icon={Shield}
                  status={getVerificationStatus('police_clearance_lk')}
                  onAction={() => setUploadModal({ open: true, type: 'police_clearance_lk' })}
                  actionLabel="Upload Certificate"
                />
                <VerificationItem
                  title="SLTS Registration"
                  description="Sri Lanka Teacher Service registration"
                  icon={Award}
                  status={getVerificationStatus('slts_registered')}
                  onAction={() => setUploadModal({ open: true, type: 'slts_registered' })}
                  actionLabel="Upload Certificate"
                />
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Qualifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-indigo-600" />
              Teaching Qualifications
            </CardTitle>
            <CardDescription>
              Optional but recommended to stand out
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isUK ? (
              <>
                <VerificationItem
                  title="Qualified Teacher Status (QTS)"
                  description="Official UK teaching qualification"
                  icon={Award}
                  status={getVerificationStatus('qts')}
                  onAction={() => setUploadModal({ open: true, type: 'qts' })}
                  actionLabel="Upload Certificate"
                />
                <VerificationItem
                  title="PGCE"
                  description="Postgraduate Certificate in Education"
                  icon={Award}
                  status={getVerificationStatus('pgce')}
                  onAction={() => setUploadModal({ open: true, type: 'pgce' })}
                  actionLabel="Upload Certificate"
                />
              </>
            ) : (
              <VerificationItem
                title="MOE Certified Teacher"
                description="Ministry of Education certification"
                icon={Award}
                status={getVerificationStatus('moe_certified')}
                onAction={() => setUploadModal({ open: true, type: 'moe_certified' })}
                actionLabel="Upload Certificate"
              />
            )}
            <VerificationItem
              title="University Degree"
              description="Verify your academic qualifications"
              icon={Award}
              status={getVerificationStatus('degree_verified')}
              onAction={() => setUploadModal({ open: true, type: 'degree_verified' })}
              actionLabel="Upload Certificate"
            />
          </CardContent>
        </Card>
        
        {/* Actions */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Back to Profile
          </Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => navigate(`/tutor/${tutorId}/dashboard`)}
          >
            Go to Dashboard <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
      
      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={uploadModal.open}
        onClose={() => setUploadModal({ open: false, type: null })}
        documentType={uploadModal.type}
        tutorId={tutorId}
        onSuccess={() => refetchData()}
      />
    </div>
  );
}

// Helper function to calculate trust score
function calculateTrustScore(tutor, verifications) {
  if (!tutor) return 0;
  
  let score = 0;
  const verificationsArray = Array.isArray(verifications) ? verifications : [];
  const approvedTypes = verificationsArray
    .filter(v => v.status === 'approved')
    .map(v => v.type);
  
  // Profile photo (15%)
  if (tutor.profile_image && !tutor.profile_image.includes('ui-avatars')) {
    score += 15;
  }
  
  // Photo verified (10%)
  if (approvedTypes.includes('photo_verified')) {
    score += 10;
  }
  
  // Background check (35%)
  const hasBackgroundCheck = approvedTypes.some(t => 
    ['dbs_basic', 'dbs_enhanced', 'dbs_enhanced_barred', 'police_clearance_lk', 'slts_registered'].includes(t)
  );
  if (hasBackgroundCheck) {
    score += 35;
  }
  
  // Teaching qualification (20%)
  const hasQualification = approvedTypes.some(t => 
    ['qts', 'pgce', 'moe_certified', 'ncoe_diploma'].includes(t)
  );
  if (hasQualification) {
    score += 20;
  }
  
  // Degree (15%)
  if (approvedTypes.includes('degree_verified')) {
    score += 15;
  }
  
  // Identity verified badge (10%)
  if (tutor.badges?.includes('identity_verified')) {
    score += 10;
  }
  
  return Math.min(score, 100);
}
